import { Injectable, signal } from '@angular/core';
import { TokenClaims } from '@sal/interfaces';
import { TenantRoles } from '@sal/shared/domain/value-objects';
import { buildTenantHostname, matchTenantHostname } from '../../shared/utils/tenant-hostname.util';

export const SAL_TENANT_STORAGE_KEY = 'sal-tenant';

export type ActiveTenantRole = TenantRoles.SAG | TenantRoles.SE | TenantRoles.SHS;

export interface StoredTenantContext {
    tenantRole: ActiveTenantRole;
}

@Injectable({
    providedIn: 'root'
})
export class TenantAuthorizationService {
    private readonly _activeTenantRole = signal<ActiveTenantRole | null>(this.readStoredActiveTenantRole());
    public readonly activeTenantRole = this._activeTenantRole.asReadonly();

    public getRequiredTenantRole(hostname = window.location.hostname): ActiveTenantRole | null {
        return matchTenantHostname(hostname);
    }

    public resolveActiveTenantRole(claims: Pick<TokenClaims, 'roles'> | null | undefined): ActiveTenantRole | null {
        const requiredTenantRole = this.getRequiredTenantRole();
        if (!requiredTenantRole || !this.hasAccessToTenant(claims, requiredTenantRole)) {
            return null;
        }

        return requiredTenantRole;
    }

    public hasAccessToCurrentDomain(claims: Pick<TokenClaims, 'roles'> | null | undefined): boolean {
        return this.resolveActiveTenantRole(claims) !== null;
    }

    // Picks the first tenant role from the token's role list (order reflects Entra role assignment order).
    public resolveFirstAccessibleTenantRole(
        claims: Pick<TokenClaims, 'roles'> | null | undefined
    ): ActiveTenantRole | null {
        const roles = claims?.roles ?? [];
        return roles.find(role => this.isActiveTenantRole(role)) ?? null;
    }

    public buildTenantRedirectUrl(tenantRole: ActiveTenantRole, targetPath = '', location = window.location): string {
        const targetHostname = buildTenantHostname(location.hostname, tenantRole);
        const origin = `${location.protocol}//${targetHostname}${location.port ? `:${location.port}` : ''}`;
        return `${origin}${targetPath}`;
    }

    public storeActiveTenantRole(tenantRole: ActiveTenantRole): void {
        const context: StoredTenantContext = { tenantRole };
        sessionStorage.setItem(SAL_TENANT_STORAGE_KEY, JSON.stringify(context));
        this._activeTenantRole.set(tenantRole);
    }

    public clearActiveTenantRole(): void {
        sessionStorage.removeItem(SAL_TENANT_STORAGE_KEY);
        this._activeTenantRole.set(null);
    }

    public readActiveTenantRole(): ActiveTenantRole | null {
        const tenantRole = this.readStoredActiveTenantRole();
        this._activeTenantRole.set(tenantRole);
        return tenantRole;
    }

    private readStoredActiveTenantRole(): ActiveTenantRole | null {
        try {
            const tenantData = sessionStorage.getItem(SAL_TENANT_STORAGE_KEY);
            if (!tenantData) {
                return null;
            }

            const context = JSON.parse(tenantData) as Partial<StoredTenantContext>;
            return this.isActiveTenantRole(context.tenantRole) ? context.tenantRole : null;
        } catch {
            return null;
        }
    }

    private hasAccessToTenant(
        claims: Pick<TokenClaims, 'roles'> | null | undefined,
        tenantRole: ActiveTenantRole
    ): boolean {
        const roles = claims?.roles ?? [];
        return roles.includes(tenantRole) || roles.includes(TenantRoles.ADMIN);
    }

    private isActiveTenantRole(role: unknown): role is ActiveTenantRole {
        return role === TenantRoles.SAG || role === TenantRoles.SE || role === TenantRoles.SHS;
    }
}
